import * as Sentry from '@sentry/react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import type { WorkflowMutationEntry } from '@/components/chat/types';
import type { AgentChatDecision } from '@/services/agents/chat-stream';
import { getAgent } from '@/services/agents/config';
import { useOrgStore } from '@/stores/org-store';
import type { Agent } from '@/types/agent-types';

import type { ConversationWriteAccess } from './conversation-access';
import {
  loadAgentChatCustomInstructions,
  persistAgentChatCustomInstructions,
} from './custom-instructions-popover';
import type { ArmedSkill } from './helpers/skill-directive';
import {
  useAgentChat,
  type AgentChatUiMessage,
  type ChatAttachmentRef,
} from './use-agent-chat';
import { useUserEvents } from './use-user-events';
import {
  pendingClarificationIn,
  pendingReviewIn,
  type RelayedRunEvent,
} from './run-event';
import { useOperatorConversationStore } from '@/stores/operator-conversation-store';
import type { AgentUserEvent } from '@/services/agents/user-events-stream';
import {
  usePromptQueueDispatch,
  type PromptQueueControls,
} from './use-prompt-queue-dispatch';
import type { ChatArtifactRef, SurfaceContext } from './types';

export interface AgentChatContextValue {
  orgId?: string;
  agentId: string;
  agent: Agent | null;
  agentLoading: boolean;
  conversationId: string | null;
  messages: AgentChatUiMessage[];
  isLoadingMessages: boolean;
  isSending: boolean;
  isPaused: boolean;
  error: string | null;
  /**
   * 'denied' when this conversation was shared with the caller view-only,
   * 'unknown' until an existing conversation's role has loaded.
   */
  writeAccess: ConversationWriteAccess;
  /** Server-side conversation title (null for new/untitled conversations). */
  conversationTitle: string | null;
  /** Question the composer is showing instead of a text box, if any. */
  pendingDecision: AgentChatDecision | null;
  /** Resolves false when the send was refused outright (nothing went out). */
  sendMessage: (
    text: string,
    attachments?: ChatAttachmentRef[],
  ) => Promise<boolean | void>;
  answerDecision: (optionIndex: number) => void;
  answerDecisionWithText: () => void;
  dismissDecision: () => void;
  stopTurn: () => void;
  pauseTurn: () => void;
  resumeTurn: () => void;
  clearMessages: () => void;
  onOpenArtifact?: (artifactId: string) => void;
  onOpenWorkflow?: (workflowId: string, runId?: string) => void;
  onApplyMutations?: (
    mutations: WorkflowMutationEntry[],
    targetWorkflowId?: string | null,
    applyKey?: string,
  ) => boolean | void;
  applyMutationsDisabled?: boolean;
  applyMutationsDisabledReason?: string;
  onRunWorkflow?: (targetWorkflowId?: string | null) => Promise<string | null>;
  /**
   * Workflow whose canvas is on screen beside this chat, if any. Chat
   * surfaces that would duplicate what the canvas already shows — live run
   * progress, chiefly — suppress themselves against it.
   */
  visibleWorkflowId: string | null;
  /**
   * The node question this conversation is waiting on, relayed by the
   * operator and not yet closed by an outcome row. The reply is an ordinary
   * message; the operator delivers it.
   */
  pendingClarification: RelayedRunEvent | null;
  /**
   * The draft a node paused for this conversation's review, relayed by the
   * operator and not yet approved. Feedback and approval are ordinary
   * messages; the operator passes them to the node.
   */
  pendingReview: RelayedRunEvent | null;
  /** True while the composer holds an unsent draft; a waiting question's countdown holds too. */
  composerDrafting: boolean;
  setComposerDrafting: (drafting: boolean) => void;
  /** Apply pending mutations for the given message. */
  applyMutations: (
    messageLocalId: string,
    mutations: WorkflowMutationEntry[],
    targetWorkflowId?: string | null,
  ) => void;
  customInstructions: string;
  setCustomInstructions: (value: string) => void;
  /** Org skill armed for this conversation (one at a time); every send
   * carries a load_skill directive for it until it is disarmed. */
  armedSkill: ArmedSkill | null;
  setArmedSkill: (skill: ArmedSkill | null) => void;
  composerRef: RefObject<HTMLTextAreaElement | null>;
  focusComposer: () => void;
  /**
   * Returns false when no composer is mounted to receive the draft (e.g.
   * a decision panel has PromptBar's spot) -- callers holding the text,
   * like the queue strip's click-to-edit, keep it instead of losing it.
   */
  setComposerDraft: (value: string) => boolean;
  registerComposerDraftSetter: (setter: (value: string) => void) => () => void;
  promptQueue: PromptQueueControls;
}

export const AgentChatContext = createContext<AgentChatContextValue | null>(
  null,
);

export function useAgentChatContext(): AgentChatContextValue {
  const value = useContext(AgentChatContext);
  if (!value) {
    throw new Error(
      'useAgentChatContext must be used within AgentChatProvider',
    );
  }
  return value;
}

export interface AgentChatProviderProps {
  agentId: string;
  conversationId?: string | null;
  onConversationChange?: (id: string) => void;
  surfaceContext?: SurfaceContext | null;
  /**
   * The workflow canvas this chat is grounded in, independent of whether
   * `surfaceContext` has resolved yet -- see the same field on
   * UseAgentChatOptions for why this matters. It may outlive the pane: a
   * host that keeps grounding after the user dismisses the canvas still
   * passes it here, and says so with `canvasVisible` below.
   */
  openWorkflowId?: string | null;
  /**
   * Whether the canvas `openWorkflowId` names is actually rendered beside
   * the chat. A host that keeps the agent grounded on a workflow whose pane
   * the user has dismissed passes false, so the chat surfaces that defer to
   * the canvas -- the workflow card's link, live run progress -- come back
   * while nothing is on screen. Defaults to true: elsewhere an open workflow
   * is a visible one.
   */
  canvasVisible?: boolean;
  /** See the same field on UseAgentChatOptions. */
  surfaceResolving?: boolean;
  /**
   * Pins the conversation to one model. No product surface sets it -- the
   * chat runs on the agent's configured model; it stays for internal callers.
   */
  modelOverride?: string | null;
  onArtifact?: (a: ChatArtifactRef) => void;
  /**
   * Every artifact frame the stored transcript carries, in order, when a
   * conversation loads. What the conversation produced -- not a signal to
   * show anything, unlike onArtifact.
   */
  onArtifactHistory?: (artifacts: ChatArtifactRef[]) => void;
  onOpenArtifact?: (artifactId: string) => void;
  onOpenWorkflow?: (workflowId: string, runId?: string) => void;
  /**
   * Called when the user clicks "Apply" on a canvas mutation confirm card.
   * Return `false` to signal the apply was blocked or failed outright.
   */
  onApplyMutations?: (
    mutations: WorkflowMutationEntry[],
    targetWorkflowId?: string | null,
    applyKey?: string,
  ) => boolean | void;
  applyMutationsDisabled?: boolean;
  applyMutationsDisabledReason?: string;
  onRunStarted?: (runId: string) => void;
  /** Called when the agent requests a canvas workflow run. */
  onRunWorkflow?: (targetWorkflowId?: string | null) => Promise<string | null>;
  onTurnComplete?: () => void;
  /** See the same field on UseAgentChatOptions. */
  conversationLinkResolving?: boolean;
  children: ReactNode;
}

export function AgentChatProvider({
  agentId,
  conversationId,
  onConversationChange,
  surfaceContext,
  openWorkflowId,
  canvasVisible = true,
  surfaceResolving,
  modelOverride,
  onArtifact,
  onArtifactHistory,
  onOpenArtifact,
  onOpenWorkflow,
  onApplyMutations,
  applyMutationsDisabled,
  applyMutationsDisabledReason,
  onRunWorkflow,
  onRunStarted,
  onTurnComplete,
  conversationLinkResolving,
  children,
}: AgentChatProviderProps) {
  const currentOrg = useOrgStore((s) => s.currentOrg);
  const orgId = currentOrg?.orgid;

  const [agent, setAgent] = useState<Agent | null>(null);
  const [agentLoading, setAgentLoading] = useState(true);

  const [customInstructions, setCustomInstructionsState] = useState<string>(
    loadAgentChatCustomInstructions,
  );
  const setCustomInstructions = useCallback((value: string) => {
    setCustomInstructionsState(value);
    persistAgentChatCustomInstructions(value);
  }, []);

  // In-memory by design: an armed skill is a per-session choice (the
  // backend's skill mounts already persist per conversation), unlike the
  // custom instructions, which are a standing preference.
  const [armedSkill, setArmedSkill] = useState<ArmedSkill | null>(null);

  const isControlled = conversationId !== undefined;
  const [internalConversationId, setInternalConversationId] = useState<
    string | null
  >(conversationId ?? null);
  const effectiveConversationId = isControlled
    ? (conversationId ?? null)
    : internalConversationId;

  const handleConversationChange = useCallback(
    (id: string) => {
      if (!isControlled) setInternalConversationId(id);
      onConversationChange?.(id);
    },
    [isControlled, onConversationChange],
  );

  useEffect(() => {
    if (!orgId || !agentId) return;
    let cancelled = false;
    (async () => {
      setAgentLoading(true);
      try {
        const a = await getAgent(orgId, agentId);
        if (!cancelled) setAgent(a);
      } catch (e) {
        Sentry.captureException(e, {
          extra: { context: 'agent-chat-provider-load-agent', orgId, agentId },
        });
        if (!cancelled) setAgent(null);
      } finally {
        if (!cancelled) setAgentLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, agentId]);

  const {
    messages,
    isLoadingMessages,
    isSending,
    isPaused,
    error,
    writeAccess,
    conversationTitle,
    adoptedConversationId,
    pendingDecision,
    sendMessage,
    answerDecision,
    answerDecisionWithText,
    dismissDecision,
    stopTurn,
    pauseTurn,
    resumeTurn,
    clearMessages,
    applyMutations,
    canvasWorkflowId,
    reloadMessages,
    adoptMintedConversation,
  } = useAgentChat({
    orgId,
    agentId,
    conversationId: effectiveConversationId,
    onConversationChange: handleConversationChange,
    surfaceContext,
    openWorkflowId,
    surfaceResolving,
    modelOverride,
    customInstructions,
    armedSkill,
    onArtifact,
    onArtifactHistory,
    onRunStarted,
    onTurnComplete,
    onApplyMutations,
    onRunWorkflow,
    conversationLinkResolving,
  });

  const promptQueue = usePromptQueueDispatch({
    orgId,
    agentId,
    conversationId: effectiveConversationId,
    adoptedConversationId,
    isSending,
    error,
    pendingDecision,
    writeAccess,
    sendMessage,
    stopTurn,
  });

  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const focusComposer = useCallback(() => {
    composerRef.current?.focus();
  }, []);

  // Every run the user owns reports here. An event for this conversation
  // re-reads the transcript, which now holds the relayed row; a wait held
  // by another chat is still answerable from here through the operator.
  const pendingClarification = useMemo(
    () => pendingClarificationIn(messages),
    [messages],
  );
  const pendingReview = useMemo(() => pendingReviewIn(messages), [messages]);
  const [composerDrafting, setComposerDrafting] = useState(false);
  const requestAttention = useOperatorConversationStore(
    (state) => state.requestAttention,
  );
  const conversationIdRef = useRef(effectiveConversationId);
  useEffect(() => {
    conversationIdRef.current = effectiveConversationId;
  }, [effectiveConversationId]);
  const handleUserEvent = useCallback(
    (event: AgentUserEvent) => {
      const forThisConversation =
        !!event.conversation_id &&
        event.conversation_id === conversationIdRef.current;
      if (
        event.type === 'clarification_requested' ||
        event.type === 'review_requested'
      ) {
        // The rail comes back for a wait it holds; one held by another
        // chat would open onto a conversation that says nothing about it.
        if (forThisConversation) {
          reloadMessages();
          requestAttention();
        }
        return;
      }
      if (
        forThisConversation &&
        (event.type === 'clarification_resolved' ||
          event.type === 'review_resolved' ||
          event.type === 'review_reply' ||
          event.type === 'run_completed' ||
          event.type === 'run_failed' ||
          event.type === 'run_stopped')
      ) {
        reloadMessages();
      }
    },
    [reloadMessages, requestAttention],
  );
  // On a reconnect the open conversation may have gained rows the stream
  // never announced; re-read it.
  const streamOpensRef = useRef(0);
  const handleStreamOpen = useCallback(() => {
    streamOpensRef.current += 1;
    if (streamOpensRef.current > 1) reloadMessages();
  }, [reloadMessages]);
  useUserEvents({
    orgId,
    agentId,
    enabled: writeAccess !== 'denied',
    onOpen: handleStreamOpen,
    onEvent: handleUserEvent,
  });

  // Surfaces that start runs outside the chat (the canvas Run button) read
  // this to tell the run where its events go; a new chat gets an id minted
  // for it so the run's first question can be the first thing in it.
  const setOperatorLink = useOperatorConversationStore(
    (state) => state.setLink,
  );
  useEffect(() => {
    if (!agentId) return;
    setOperatorLink({
      agentId,
      conversationId: effectiveConversationId,
      mint: () => {
        const id = crypto.randomUUID().replace(/-/g, '');
        adoptMintedConversation(id);
        return id;
      },
    });
    return () => setOperatorLink(null);
  }, [
    agentId,
    effectiveConversationId,
    adoptMintedConversation,
    setOperatorLink,
  ]);

  // canvasWorkflowId (from useAgentChat) names an unsaved canvas by its
  // draft key rather than a flat 'new', so an unsaved canvas's own
  // proposals and runs can still be told apart from both "no canvas open"
  // and a different draft the user has since switched to without ever
  // saving. The run card treats a mismatch as "stays visible", which is
  // the safe way to be wrong either way. A host may keep the agent grounded
  // on a canvas it no longer renders; with nothing on screen there is
  // nothing for the chat to defer to.
  const visibleWorkflowId = canvasVisible ? canvasWorkflowId : null;

  // The prompt bar keeps its draft local so keystrokes don't re-render the
  // message list; prefilling reaches it through this registered setter.
  const draftSetterRef = useRef<((value: string) => void) | null>(null);
  const registerComposerDraftSetter = useCallback(
    (setter: (value: string) => void) => {
      draftSetterRef.current = setter;
      return () => {
        if (draftSetterRef.current === setter) draftSetterRef.current = null;
      };
    },
    [],
  );
  const setComposerDraft = useCallback((value: string) => {
    const setter = draftSetterRef.current;
    if (!setter) return false;
    setter(value);
    composerRef.current?.focus();
    return true;
  }, []);

  const value = useMemo<AgentChatContextValue>(
    () => ({
      orgId,
      agentId,
      agent,
      agentLoading,
      conversationId: effectiveConversationId,
      messages,
      isLoadingMessages,
      isSending,
      isPaused,
      error,
      writeAccess,
      conversationTitle,
      pendingDecision,
      sendMessage,
      answerDecision,
      answerDecisionWithText,
      dismissDecision,
      // The queue's wrapper: stopping a turn by hand also holds the queue,
      // so the interruption isn't immediately undone by auto-dispatch.
      stopTurn: promptQueue.stopTurn,
      pauseTurn,
      resumeTurn,
      clearMessages,
      promptQueue,
      pendingClarification,
      pendingReview,
      composerDrafting,
      setComposerDrafting,
      onOpenArtifact,
      onOpenWorkflow,
      onApplyMutations,
      applyMutationsDisabled,
      applyMutationsDisabledReason,
      applyMutations,
      onRunWorkflow,
      visibleWorkflowId,
      customInstructions,
      setCustomInstructions,
      armedSkill,
      setArmedSkill,
      composerRef,
      focusComposer,
      setComposerDraft,
      registerComposerDraftSetter,
    }),
    [
      orgId,
      agentId,
      agent,
      agentLoading,
      effectiveConversationId,
      messages,
      isLoadingMessages,
      isSending,
      isPaused,
      error,
      writeAccess,
      conversationTitle,
      pendingDecision,
      sendMessage,
      answerDecision,
      answerDecisionWithText,
      dismissDecision,
      pauseTurn,
      resumeTurn,
      clearMessages,
      promptQueue,
      pendingClarification,
      pendingReview,
      composerDrafting,
      setComposerDrafting,
      applyMutations,
      onOpenArtifact,
      onOpenWorkflow,
      onApplyMutations,
      applyMutationsDisabled,
      applyMutationsDisabledReason,
      onRunWorkflow,
      visibleWorkflowId,
      customInstructions,
      setCustomInstructions,
      armedSkill,
      focusComposer,
      setComposerDraft,
      registerComposerDraftSetter,
    ],
  );

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  );
}
